import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Copy, Search, Heart, GitCompare, ShoppingCart, ChevronDown } from 'lucide-react';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useToast } from '../components/ui/Toast';
import { useScanInput } from '../hooks/useScanInput';
import { useCartStore, type PriceInfo, type Origin, type CartLine } from './cartStore';
import {
  deliveryEta,
  promiseEta,
  cutoffCountdown,
  todayCutoff,
  useMinuteTick,
  type DeliveryCalendar,
  type DeliverySlot,
  type PromiseRule,
  type Eta,
} from '../utils/deliveryPromise';
import { brandStyle, PartThumb } from './shared';
import { CartPanel } from './CartPanel';
import { formatEUR, specLabel, specValue } from '../lib/format';
import { ArticleImage } from '../components/ui/ArticleImage';

const eur = (n: number): string => formatEUR(n);
const pct = (n: number) => n.toFixed(0) + '%';

interface CatalogHit {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  manufacturerRef: string | null;
  stock: number;
  /** Part photo and brand logo (TecDoc media, served behind the session) — as at the counter. */
  imageUrl?: string | null;
  brandImageUrl?: string | null;
  /** TecDoc criteria (size, type…) — the line a buyer reads to know it is the right part. */
  specifications?: Array<{ label: string; value: string; unit?: string | null }>;
  /** Root → leaf category chain, for the category filter — as at the counter. */
  categoryPath?: Array<{ id: string; name: string }>;
  /** SUPPLIER = a part we don't hold, offered from a supplier's shelf for D+1/D+2. */
  source?: 'CATALOG' | 'SUPPLIER';
  /** No supplier identity and no cost ever reach a B2B session — see routes/catalog.ts. */
  supplier?: {
    availableQty: number | null;
    leadTimeDays: number | null;
    /** The supplier's own dates (warehouse stripped for B2B) — tooltip information. */
    deliverySlots?: DeliverySlot[];
    /** OUR promise rule for this supplier (2.1) — what the delivery line is dated with. No identity. */
    promiseRule?: PromiseRule;
    indicativeSellPriceHT: number | null;
    /** The supplier's internal code for this offer — WHICH offer, never WHOSE. Safe for B2B, and
     *  what lets the server find the same row again without the buyer naming a supplier. */
    supplierArticleNo?: string | null;
  };
}
interface Availability {
  productId: string;
  internalStock: number;
  price: PriceInfo | null; // null when the product can't be priced
  // Aggregate supply signal. B2B callers deliberately get no supplier identity or cost — just
  // "can it be delivered, and how soon" (0 = on our shelf, null = nobody can supply it).
  supplierAvailable: boolean;
  supplierLeadDays: number | null;
  supplierSlots?: DeliverySlot[];
  /** OUR promise rule for the supplier this line would be bought from (2.1). */
  supplierPromise?: PromiseRule;
  /** How many units the supplier holds — a quantity, never an identity. */
  supplierQty: number | null;
  /**
   * The price of a unit SOURCED FROM THE SUPPLIER: the same cascade against THEIR cost, through
   * this customer's own grid. A selling price, so it is safe here; the cost behind it never is.
   */
  externalPrice: PriceInfo | null;
}
type AvailState = 'ok' | 'warn' | 'out';
/** Rows with no category, grouped under one filter entry. */
const UNFILED = '__unfiled__';
const SPEC_SEP = '\u0001';
interface Row extends CatalogHit {
  price?: PriceInfo;
  /**
   * A SUPPLIER row's price, for DISPLAY only.
   *
   * It has no product record, so `/catalog/availability` cannot price it and `price` stays
   * undefined — which is why these rows used to read "— HT" and their cart button did nothing.
   * The search already returns the figure, priced through this customer's own grid; the real
   * PriceInfo is fetched at the moment the line is added, once the record exists.
   */
  indicativePriceHT?: number | null;
  externalPrice?: PriceInfo | null;
  internalStock?: number;
  supplierQty?: number | null;
  /** Ranking/state only — the date shown comes from promiseRule (our rule). */
  leadDays?: number | null;
  deliverySlots?: DeliverySlot[];
  promiseRule?: PromiseRule;
  avail: AvailState;
}

/**
 * B2B customer portal catalogue — MDS "Portal" composition: filters | results | cart.
 * Self-service buyer sees negotiated (contract-discount) pricing and availability;
 * no internal cost/margin. Search term arrives via the header (?q=…).
 */
type SearchMode = 'ALL' | 'TEXT' | 'EAN' | 'SKU';
const SEARCH_MODES: readonly SearchMode[] = ['ALL', 'TEXT', 'EAN', 'SKU'];

/* The full search answers in waves: suppliers are still being asked about some equivalents
   when the first answer comes back (`supplierPending`). Ask again a few times, like the counter
   does — each round counts against b2bSearchRateLimit, so the count stays small. Since each
   supplier gets 3 s before the first answer goes out (a slower one's rows arrive on a later
   round), the rounds are closer together: 5 searches in 20 s, well inside 30/min. */
const MAX_OFFER_REFRESH = 4;
const OFFER_REFRESH_MS = 5_000;

const availOf = (internalStock?: number, leadDays?: number | null): AvailState =>
  internalStock && internalStock > 0 ? 'ok' : leadDays != null ? 'warn' : 'out';

export default function CustomerCatalog() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  const urlMode = params.get('mode');
  const mode: SearchMode =
    urlMode && SEARCH_MODES.includes(urlMode as SearchMode) ? (urlMode as SearchMode) : 'ALL';
  const { showApiError, push } = useToast();
  /** The supplier row currently being turned into an order line — its button spins meanwhile. */
  const [sourcing, setSourcing] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  const customerId = user?.customerId ?? null;
  const add = useCartStore((s) => s.add);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [supplierPending, setSupplierPending] = useState(0);
  /** The term the newest search asked about — an answer for any other term is stale. */
  const searchedTerm = useRef('');
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [brandFilter, setBrandFilter] = useState('');
  // The counter's other filters (category, availability, technical criteria), same rules.
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedAvail, setSelectedAvail] = useState<Set<AvailState>>(new Set());
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());

  useScanInput({ onScan: (text) => void runSearch(text, 'EAN') });

  async function runSearch(text: string, searchMode: SearchMode = 'ALL', round = 0) {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    searchedTerm.current = text;
    if (!text.trim()) {
      setRows([]);
      setSupplierPending(0);
      return;
    }
    if (round === 0) {
      setLoading(true);
      setSelectedBrands(new Set());
      setSelectedCategories(new Set());
      setSelectedAvail(new Set());
      setSelectedSpecs(new Set());
      setSupplierPending(0);
    }
    try {
      const { data: body } = await apiClient.get<{ hits: CatalogHit[]; supplierPending: number }>(
        // supplier=1: B2B buyers may order a supplier's external stock for D+1/D+2. The API
        // withholds supplier identity and our cost from every B2B role and prices the row through
        // THEIR grid, so what a customer sees is a part, a delay, and their own price.
        // offer=full: the equivalents family too — a Bosch number also finds the Mann and Filtron
        // parts that are actually available, as it does at the counter.
        `/catalog/search?q=${encodeURIComponent(text)}&mode=${searchMode}&supplier=1&offer=full&meta=1`
      );
      // The buyer has searched something else meanwhile: this answer is for an old question.
      if (searchedTerm.current !== text) return;
      const hits = body.hits;
      const pending = body.supplierPending ?? 0;
      setSupplierPending(round < MAX_OFFER_REFRESH ? pending : 0);
      if (pending > 0 && round < MAX_OFFER_REFRESH) {
        refreshTimer.current = setTimeout(() => {
          if (searchedTerm.current === text) void runSearch(text, searchMode, round + 1);
        }, OFFER_REFRESH_MS);
      }
      let enriched: Row[] = hits.map((h) => ({ ...h, avail: 'out' as AvailState }));
      // External-stock rows have no product record — their id is a synthetic key, so they must be
      // kept out of the availability lookup (which resolves product UUIDs) and priced from the
      // quote that already came back with the search.
      const catalogHits = hits.filter((h) => h.source !== 'SUPPLIER');
      const supplierRow = (h: CatalogHit): Row => ({
        ...h,
        internalStock: 0,
        leadDays: h.supplier?.leadTimeDays ?? null,
        deliverySlots: h.supplier?.deliverySlots ?? [],
        promiseRule: h.supplier?.promiseRule,
        indicativePriceHT: h.supplier?.indicativeSellPriceHT ?? null,
        avail: (h.supplier?.availableQty ?? 0) > 0 ? 'warn' : 'out',
      });
      if (catalogHits.length > 0) {
        try {
          const ids = catalogHits.map((h) => h.id).join(',');
          const { data: avs } = await apiClient.get<Availability[]>(
            `/catalog/availability?ids=${encodeURIComponent(ids)}${customerId ? `&customerId=${customerId}` : ''}`
          );
          const byId = new Map(avs.map((a) => [a.productId, a]));
          enriched = hits.map((h) => {
            if (h.source === 'SUPPLIER') return supplierRow(h);
            const av = byId.get(h.id);
            /* TWO ROADS TO THE SAME SUPPLIER FIGURE, and the row takes whichever arrives.
               `/catalog/availability` quotes through `supplier_article_mapping` — the link built
               when we have actually bought that part through the API — so a reference we carry
               but have never ordered has no mapping and comes back with no supplier stock at
               all. The SEARCH has already asked the same suppliers by manufacturer number and
               attached what came back (CatalogService.search merges the live candidate onto our
               row), so for exactly those parts it is the only one of the two that knows the
               answer. Preferring the mapped figure and falling back to the search's keeps a
               part orderable for D+1 instead of reading "on request" beside a supplier who is
               holding it. */
            const leadDays = av?.supplierLeadDays ?? h.supplier?.leadTimeDays ?? null;
            const supplierQty = av?.supplierQty ?? h.supplier?.availableQty ?? null;
            return {
              ...h,
              price: av?.price ?? undefined,
              externalPrice: av?.externalPrice ?? null,
              // The supplier-sourced price, when only the search knows it. Display only — the
              // line is priced again server-side at the moment it is added.
              indicativePriceHT:
                av?.externalPrice?.finalUnitPriceHT ?? h.supplier?.indicativeSellPriceHT ?? null,
              internalStock: av?.internalStock,
              supplierQty,
              leadDays,
              deliverySlots: av?.supplierSlots?.length
                ? av.supplierSlots
                : (h.supplier?.deliverySlots ?? []),
              promiseRule: av?.supplierPromise ?? h.supplier?.promiseRule,
              avail: availOf(av?.internalStock, leadDays),
            };
          });
        } catch {
          /* enrichment failed — still show results, just no prices */
        }
      } else {
        enriched = hits.map((h) => (h.source === 'SUPPLIER' ? supplierRow(h) : (h as Row)));
      }
      setRows(enriched);
      if (round > 0) return; // a follow-up only widens the list: no scan feedback
      if (searchMode === 'EAN' && enriched.length === 0) {
        push({
          tone: 'error',
          title: t('counter.productNotFound'),
          message: t('counter.noBarcodeMatch', { code: text }),
        });
      }
    } catch (err) {
      if (round === 0) showApiError(err); // a failed follow-up keeps the list already shown
    } finally {
      if (round === 0) setLoading(false);
    }
  }

  // Leaving the page must not leave a follow-up search ticking.
  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    []
  );

  useEffect(() => {
    void runSearch(q, mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode, customerId]);

  const brandFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) if (r.brand) counts.set(r.brand, (counts.get(r.brand) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  /** Top-level category, counted — the first cut of a full offer (filter vs filter set…). */
  const categoryFacets = useMemo(() => {
    const tops = new Map<string, { id: string; name: string; n: number }>();
    for (const r of rows) {
      const top = r.categoryPath?.[0] ?? { id: UNFILED, name: t('portal.catalogue.uncategorised') };
      const e = tops.get(top.id) ?? { id: top.id, name: top.name, n: 0 };
      e.n += 1;
      tops.set(top.id, e);
    }
    return [...tops.values()].sort((a, b) =>
      a.id === UNFILED ? 1 : b.id === UNFILED ? -1 : b.n - a.n || a.name.localeCompare(b.name)
    );
  }, [rows, t]);

  /** Technical criteria that actually divide the list (2–12 distinct values), as at the counter. */
  const specFacets = useMemo(() => {
    const byLabel = new Map<string, { title: string; vals: Map<string, number> }>();
    for (const r of rows) {
      for (const sp of r.specifications ?? []) {
        const g = byLabel.get(sp.label) ?? {
          title: specLabel(sp),
          vals: new Map<string, number>(),
        };
        g.vals.set(specValue(sp), (g.vals.get(specValue(sp)) ?? 0) + 1);
        byLabel.set(sp.label, g);
      }
    }
    return [...byLabel.entries()]
      .filter(([, g]) => g.vals.size >= 2 && g.vals.size <= 12)
      .map(([label, { title, vals }]) => ({
        label,
        title,
        values: [...vals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
      }))
      .sort((a, b) => a.values.length - b.values.length || a.label.localeCompare(b.label))
      .slice(0, 6);
  }, [rows]);

  const availCounts = useMemo(
    () => ({
      ok: rows.filter((r) => r.avail === 'ok').length,
      warn: rows.filter((r) => r.avail === 'warn').length,
      out: rows.filter((r) => r.avail === 'out').length,
    }),
    [rows]
  );

  const visibleRows = useMemo(() => {
    // OR inside one criterion, AND across criteria — a second value in a group widens the list.
    const wanted = new Map<string, Set<string>>();
    for (const key of selectedSpecs) {
      const [label, value] = key.split(SPEC_SEP);
      wanted.set(label, (wanted.get(label) ?? new Set<string>()).add(value));
    }
    // Feedback 20 Oct 2026, 1.5: offers nobody can supply (zero stock, no quote) are not shown.
    return rows
      .filter((r) => r.avail !== 'out')
      .filter((r) => selectedBrands.size === 0 || (r.brand && selectedBrands.has(r.brand)))
      .filter((r) => {
        if (selectedCategories.size === 0) return true;
        const path = r.categoryPath ?? [];
        if (path.length === 0) return selectedCategories.has(UNFILED);
        return path.some((c) => selectedCategories.has(c.id));
      })
      .filter((r) => selectedAvail.size === 0 || selectedAvail.has(r.avail))
      .filter((r) => {
        for (const [label, values] of wanted) {
          const own = (r.specifications ?? []).filter((x) => x.label === label).map(specValue);
          if (!own.some((v) => values.has(v))) return false;
        }
        return true;
      });
  }, [rows, selectedBrands, selectedCategories, selectedAvail, selectedSpecs]);

  /* The delivery rule, from the server, so the buyer is quoted the same date the counter and the
     order line will use. Defaults bridge the fetch; they are never what gets frozen. */
  const [calendar, setCalendar] = useState<DeliveryCalendar>({
    cutoff: '14:20',
    deliveryTime: '13:00',
    holidays: [],
  });
  /* Feedback 2.1 — each supplier's cut-off for the header, unnamed on the portal (INV-23). */
  const [supplierCutoffs, setSupplierCutoffs] = useState<PromiseRule[]>([]);
  const nowMs = useMinuteTick();
  useEffect(() => {
    apiClient
      .get<DeliveryCalendar & { supplierCutoffs?: PromiseRule[] }>('/meta/sales-config')
      .then(({ data }) => {
        setCalendar({
          cutoff: data.cutoff,
          deliveryTime: data.deliveryTime,
          holidays: data.holidays ?? [],
        });
        setSupplierCutoffs(data.supplierCutoffs ?? []);
      })
      .catch(() => {
        /* non-blocking: dates fall back to the defaults above */
      });
  }, []);

  const toggleIn =
    <T,>(set: (fn: (cur: Set<T>) => Set<T>) => void) =>
    (v: T) =>
      set((cur) => {
        const next = new Set(cur);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        return next;
      });
  const toggleCategory = toggleIn<string>(setSelectedCategories);
  const toggleAvail = toggleIn<AvailState>(setSelectedAvail);
  const toggleSpec = toggleIn<string>(setSelectedSpecs);

  const toggleBrand = (b: string) =>
    setSelectedBrands((cur) => {
      const next = new Set(cur);
      next.has(b) ? next.delete(b) : next.add(b);
      return next;
    });

  /* The plain "add" beside the price, which is the one most buyers use. It composes the OBVIOUS
     split rather than asserting an origin: our shelf up to what is on it, the shortfall from the
     supplier at the supplier's own price. Sending the whole quantity as INTERNAL because one unit
     happened to be in the building made the server re-source all of it externally, at our shelf
     price. The per-origin composer below is still there for a deliberate split. */
  const addRow = async (r: Row, qty: number) => {
    /* A SUPPLIER row is a part we do not hold, offered from a supplier's shelf. There is no
       product record behind it, so there is nothing to price and nothing to hang an order line
       off — which is why this used to return here and the button did nothing at all.

       Same two steps the counter takes: ask the server to find (or create, unlisted) the record
       for that offer, then price it through the ordinary availability call. The supplier is never
       named in either direction — the row carries the supplier's article code, and the server
       chooses whose offer it was. */
    if (r.source === 'SUPPLIER') {
      setSourcing(r.id);
      try {
        /* PRICE IT, DO NOT MINT IT. Filling a basket must leave nothing behind: the record is
           made at checkout, by POST /orders. `/offers/preview` is the read-only twin of
           `/offers/resolve` — same service, same projection (no supplier, no cost). */
        const { data: q } = await apiClient.post<{
          productId: string | null;
          price: PriceInfo | null;
          leadTimeDays: number | null;
          deliverySlots?: DeliverySlot[];
          promiseRule?: PromiseRule;
        }>('/catalog/offers/preview', {
          matchedNumber: r.sku,
          supplierArticleNo: r.supplier?.supplierArticleNo ?? null,
          brand: r.brand,
        });

        // ALREADY OURS — the line names the record and prices through availability, as before.
        if (q.productId) {
          const { data: av } = await apiClient.get<Availability>(
            `/catalog/products/${q.productId}/availability${customerId ? `?customerId=${customerId}` : ''}`
          );
          const price = av.externalPrice ?? av.price;
          if (!price) {
            push({ tone: 'warning', message: t('portal.catalogue.offerGone', { sku: r.sku }) });
            return;
          }
          addOrigin(
            {
              ...r,
              id: q.productId,
              price,
              externalPrice: av.externalPrice,
              source: 'CATALOG',
              leadDays: av.supplierLeadDays ?? r.leadDays,
              deliverySlots: av.supplierSlots ?? r.deliverySlots,
              promiseRule: av.supplierPromise ?? r.promiseRule,
            },
            'EXTERNAL',
            qty,
            price.finalUnitPriceHT
          );
          return;
        }

        if (!q.price) {
          push({ tone: 'warning', message: t('portal.catalogue.offerGone', { sku: r.sku }) });
          return;
        }
        // No record yet: the basket carries the OFFER. `r.id` (`sup:<code>:<number>`) is the
        // basket key only — it never reaches the server as a product.
        addOrigin(
          {
            ...r,
            price: q.price,
            externalPrice: q.price,
            leadDays: q.leadTimeDays ?? r.leadDays,
            deliverySlots: q.deliverySlots ?? r.deliverySlots,
            promiseRule: q.promiseRule ?? r.promiseRule,
          },
          'EXTERNAL',
          qty,
          q.price.finalUnitPriceHT,
          {
            matchedNumber: r.sku,
            supplierArticleNo: r.supplier?.supplierArticleNo ?? null,
            name: r.name,
            brand: r.brand ?? null,
          }
        );
      } catch (err) {
        showApiError(err);
      } finally {
        setSourcing(null);
      }
      return;
    }
    if (!r.price) return;
    const shelf = r.internalStock ?? 0;
    // The supplier's own price when either road knows it; our shelf price only as a last resort,
    // so the shortfall is not quietly sold at the price of stock we do not have.
    const extPrice =
      r.externalPrice?.finalUnitPriceHT ?? r.indicativePriceHT ?? r.price.finalUnitPriceHT;
    if (shelf > 0 && qty > shelf) {
      addOrigin(r, 'INTERNAL', shelf, r.price.finalUnitPriceHT);
      addOrigin(r, 'EXTERNAL', qty - shelf, extPrice);
      return;
    }
    if (shelf > 0) addOrigin(r, 'INTERNAL', qty, r.price.finalUnitPriceHT);
    // No shelf stock → the supplier's line, at the SUPPLIER's price: this used to pass our own,
    // which quietly overrode the external price the buyer had just read on the row.
    else addOrigin(r, 'EXTERNAL', qty, extPrice);
  };

  /** One composed origin, at that origin's own price — never merged into the other's line. */
  const addOrigin = (
    r: Row,
    source: Origin,
    qty: number,
    unitPriceHT: number,
    /** A supplier's offer with no record behind it yet — carried to the server at checkout. */
    offer?: CartLine['offer']
  ) => {
    const base = (source === 'EXTERNAL' ? r.externalPrice : r.price) ?? r.price;
    if (!base) return;
    /* The SAME part at a different unit price — our shelf price for what is here, the supplier's
       for what is ordered in. Only the HT figures were being replaced, so the line kept the TTC
       of the price it was cloned from and the cart's VAT came out as (old TTC − new HT): wrong
       in both directions, and visibly so once the external price stopped defaulting to our own.
       The VAT RATE is what the two share, so it is the rate that is carried over. */
    const vatRatio = base.finalUnitPriceHT > 0 ? base.unitPriceTTC / base.finalUnitPriceHT : 1;
    add(
      {
        productId: r.id,
        ...(offer ? { offer } : {}),
        sku: r.sku,
        name: r.name,
        brand: r.brand,
        price:
          unitPriceHT !== base.finalUnitPriceHT
            ? {
                ...base,
                finalUnitPriceHT: unitPriceHT,
                unitPriceHT,
                unitPriceTTC: Math.round(unitPriceHT * vatRatio * 100) / 100,
              }
            : base,
        source,
      },
      qty
    );
  };

  return (
    <div className="mds is-portail">
      <div className="wrap">
        {/* FILTERS */}
        <aside className="col filters" aria-label={t('portal.catalogue.filters')}>
          <div className="fgroup">
            <h3>{t('portal.catalogue.findFilter')}</h3>
            <input
              className="fsearch"
              placeholder={t('portal.catalogue.criterionName')}
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            />
          </div>
          {categoryFacets.length > 1 && (
            <div className="fgroup">
              <h3>{t('portal.catalogue.category')}</h3>
              {categoryFacets.map((c) => (
                <label className="chk" key={c.id}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.has(c.id)}
                    onChange={() => toggleCategory(c.id)}
                  />
                  <span>{c.name}</span>
                  <span className="cnt">{c.n}</span>
                </label>
              ))}
            </div>
          )}
          <div className="fgroup">
            <h3>
              {t('portal.catalogue.brand')}{' '}
              <span className="muted">{t('portal.catalogue.sortDown')}</span>
            </h3>
            {brandFacets.length === 0 && <div className="muted">—</div>}
            {brandFacets
              .filter(([b]) => b.toLowerCase().includes(brandFilter.toLowerCase()))
              .map(([b, n]) => (
                <label className="chk" key={b}>
                  <input
                    type="checkbox"
                    checked={selectedBrands.has(b)}
                    onChange={() => toggleBrand(b)}
                  />
                  <span className="badge brand" style={brandStyle(b)}>
                    {b}
                  </span>
                  <span className="cnt">{n}</span>
                </label>
              ))}
          </div>
          {rows.length > 0 && (
            <div className="fgroup">
              <h3>{t('portal.catalogue.availability')}</h3>
              {(['ok', 'warn'] as const).map((a) => (
                <label className="chk" key={a}>
                  <input
                    type="checkbox"
                    checked={selectedAvail.has(a)}
                    onChange={() => toggleAvail(a)}
                  />
                  <span>{t(`portal.catalogue.availFilter.${a}`)}</span>
                  <span className="cnt">{availCounts[a]}</span>
                </label>
              ))}
            </div>
          )}
          {specFacets.map((g) => (
            <div className="fgroup" key={g.label}>
              <h3>{g.title}</h3>
              {g.values.map(([v, n]) => {
                const key = `${g.label}${SPEC_SEP}${v}`;
                return (
                  <label className="chk" key={key}>
                    <input
                      type="checkbox"
                      checked={selectedSpecs.has(key)}
                      onChange={() => toggleSpec(key)}
                    />
                    <span>{v}</span>
                    <span className="cnt">{n}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </aside>

        {/* RESULTS */}
        <main className="col results">
          <div className="rtitle">
            <h1>{t('portal.catalogue.fullOffer')}</h1>
            <span className="muted">
              {t('portal.catalogue.references', { count: rows.length })}
            </span>
            {/* Feedback 2.1 — the cut-off of each warehouse, hours and minutes, and what is left
                before it: the garage ordering on its own sees the same clock as the counter. */}
            {supplierCutoffs.length > 0 && (
              <span className="cutoffs">
                {supplierCutoffs.map((s, i) => {
                  // Today's row of the weekly table (2026-10-29); null = no cut-off today.
                  const cut = todayCutoff(s, nowMs);
                  const left = cut ? cutoffCountdown(cut, nowMs) : null;
                  const name = t('delivery.warehouseN', { n: i + 1 });
                  return (
                    <span
                      key={i}
                      className={`cutoff${cut && !left ? ' is-passed' : left?.urgent ? ' is-urgent' : ''}`}
                      title={
                        cut
                          ? t(s.days ? 'delivery.ourRuleTable' : 'delivery.ourRule', {
                              cutoff: cut,
                              count: s.leadDays,
                            })
                          : t('delivery.noCutoffToday', { count: s.leadDays })
                      }
                    >
                      {cut
                        ? t('delivery.cutoffOf', { name, cutoff: cut })
                        : t('delivery.noCutoffOf', { name })}{' '}
                      {cut && (
                        <b>
                          {left
                            ? t('delivery.cutoffIn', { time: left.text })
                            : t('delivery.cutoffPassedShort')}
                        </b>
                      )}
                    </span>
                  );
                })}
              </span>
            )}
          </div>

          <div className="toolbar">
            <span>{t('portal.catalogue.sort')}</span>
            <span className="pill">
              {t('portal.catalogue.relevance')} <ChevronDown size={13} />
            </span>
          </div>

          <ul className="parts" aria-live="polite">
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <li className="skel-row" key={i}>
                  <div className="skel sk-thumb" />
                  <div>
                    <div className="skel sk-line sk-60" />
                    <div className="skel sk-line sk-40" style={{ marginTop: 8 }} />
                    <div className="skel sk-line sk-80" style={{ marginTop: 8 }} />
                  </div>
                  <div className="skel sk-line" style={{ height: 28 }} />
                </li>
              ))}

            {!loading && supplierPending > 0 && (
              <li role="status">
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="skel" style={{ width: 14, height: 14, borderRadius: 99 }} />
                  {t('portal.catalogue.stillChecking', { count: supplierPending })}
                </div>
              </li>
            )}

            {!loading && visibleRows.length === 0 && supplierPending === 0 && (
              <li>
                <div className="empty">
                  <div className="ic">
                    <Search size={26} />
                  </div>
                  <h2>{q ? t('portal.catalogue.noMatch') : t('portal.catalogue.searchThe')}</h2>
                  <p>
                    {q
                      ? t('portal.catalogue.noResultsFor', { query: q })
                      : t('portal.catalogue.startSearch')}
                  </p>
                </div>
              </li>
            )}

            {!loading &&
              visibleRows.map((r) => (
                <PortalPartCard
                  key={r.id}
                  r={r}
                  query={q}
                  cal={calendar}
                  busy={sourcing === r.id}
                  onAdd={(qty) => void addRow(r, qty)}
                  onAddOrigin={(src, n, unit) => addOrigin(r, src, n, unit)}
                />
              ))}
          </ul>
        </main>

        {/* CART */}
        <CartPanel />
      </div>
    </div>
  );
}

function PortalPartCard({
  r,
  query,
  cal,
  busy,
  onAdd,
  onAddOrigin,
}: {
  r: Row;
  query: string;
  /** Cut-off, delivery hour and closures — the same rule the counter and the server run. */
  cal: DeliveryCalendar;
  /** This row is being sourced from its supplier right now (a round trip, not an instant add). */
  busy?: boolean;
  onAdd: (qty: number) => void;
  onAddOrigin: (source: Origin, qty: number, unitPriceHT: number) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { push } = useToast();
  const [qty, setQty] = useState(1);
  const nowMs = useMinuteTick();

  const copySku = async () => {
    try {
      await navigator.clipboard.writeText(r.sku);
      push({ tone: 'success', message: t('portal.catalogue.copied', { sku: r.sku }) });
    } catch {
      push({ tone: 'error', message: t('portal.catalogue.couldNotCopy') });
    }
  };
  const isMatch =
    !!query &&
    [r.sku, r.manufacturerRef, r.ean].some((v) => v?.toLowerCase() === query.toLowerCase());

  // The same information line as the counter: the first TecDoc criteria (size, then type), then
  // the reference to quote. A buyer reads the size to know it is the right part.
  const specs: [string, string][] = (r.specifications ?? [])
    .slice(0, 6)
    .map((x) => [specLabel(x), specValue(x)] as [string, string]);
  if (r.manufacturerRef) specs.push([t('portal.catalogue.mfrRef'), r.manufacturerRef]);
  if (r.ean && specs.length < 7) specs.push(['EAN', r.ean]);

  const availCls =
    r.avail === 'ok' ? 'avail--ok' : r.avail === 'warn' ? 'avail--warn' : 'avail--out';
  /* Same rule and same words as the counter — a buyer choosing on delivery date must not be
     handed "Inbound · 2 d" and left to do the arithmetic the seller is spared. */
  const eta =
    r.avail === 'ok'
      ? deliveryEta(0, cal, nowMs)
      : r.leadDays != null
        ? promiseEta(r.promiseRule, cal, nowMs, { slots: r.deliverySlots, leadDays: r.leadDays })
        : null;
  const availText = eta ? eta.label : t('portal.catalogue.onRequest');
  const hasDiscount = !!r.price && r.price.discountPct > 0;

  /* THE ORIGINS this reference can be served from. The buyer picks per origin on exactly the three
     things that decide it for them: how many, what it costs, when it arrives. No supplier is
     named and no cost of ours is exposed — a B2B session never sees either. */
  const origins = useMemo(() => {
    const out: { source: Origin; available: number; unitPriceHT: number; eta: Eta }[] = [];
    if ((r.internalStock ?? 0) > 0 && r.price) {
      out.push({
        source: 'INTERNAL',
        available: r.internalStock ?? 0,
        unitPriceHT: r.price.finalUnitPriceHT,
        eta: deliveryEta(0, cal, nowMs),
      });
    }
    const extPrice =
      r.externalPrice?.finalUnitPriceHT ?? r.indicativePriceHT ?? r.price?.finalUnitPriceHT;
    if ((r.supplierQty ?? 0) > 0 && extPrice != null) {
      out.push({
        source: 'EXTERNAL',
        available: r.supplierQty ?? 0,
        unitPriceHT: extPrice,
        eta: promiseEta(r.promiseRule, cal, nowMs, {
          slots: r.deliverySlots,
          leadDays: r.leadDays,
        }),
      });
    }
    return out;
  }, [r, cal, nowMs]);
  const [originQty, setOriginQty] = useState<Partial<Record<Origin, number>>>({});
  const composedUnits = origins.reduce((n, o) => n + (originQty[o.source] ?? 0), 0);
  const composedOrigins = origins.filter((o) => (originQty[o.source] ?? 0) > 0).length;

  return (
    <li className={`part${isMatch ? ' is-match' : ''}`}>
      {isMatch && (
        <div className="matchnote">
          <Search size={14} /> {t('portal.catalogue.searchMatches', { query })}
        </div>
      )}
      <div className="main">
        <div className="idblk">
          <div className="thumb">
            <ArticleImage
              src={r.imageUrl}
              brandSrc={r.brandImageUrl}
              alt={r.brand ?? r.sku}
              size={58}
              fallback={<PartThumb />}
            />
          </div>
          <div className="idtext">
            <div className="top">
              <h3 className="sku" title={r.sku}>
                {r.sku}
              </h3>
              <span className="rowicons">
                <button
                  className="iconbtn"
                  aria-label={t('portal.catalogue.copySku', { sku: r.sku })}
                  onClick={copySku}
                >
                  <Copy size={15} />
                </button>
                <button
                  className="iconbtn"
                  aria-label={t('portal.catalogue.viewSku', { sku: r.sku })}
                  onClick={() => navigate(`/catalog/${r.id}`)}
                >
                  <GitCompare size={15} />
                </button>
                <button
                  className="iconbtn"
                  aria-label={t('portal.catalogue.favouriteSku', { sku: r.sku })}
                >
                  <Heart size={15} />
                </button>
              </span>
            </div>
            <div className="pname" title={r.name}>
              {r.name}
            </div>
            <div className="specs">
              {specs.map(([k, v]) => (
                <span className="chip" key={k} title={`${k} ${v}`}>
                  {k} <b>{v}</b>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div>
          {/* The logo is how a mechanic recognises a make; logos can be missing, so the name stays. */}
          {r.brandImageUrl && (
            <ArticleImage src={r.brandImageUrl} alt={r.brand ?? ''} size={28} fallback={null} />
          )}
          <span className="badge brand" style={brandStyle(r.brand)}>
            {r.brand ?? '—'}
          </span>
          <div style={{ marginTop: 8 }}>
            <span className={`avail ${availCls}`}>
              <span className="dot" />
              {availText}
            </span>
          </div>
        </div>

        <div>
          <div className="priceblk client">
            <div className="pv tnum">
              {/* A supplier row has no product record to price, so its figure comes from the
                  search itself — the same cascade, run on the supplier's cost through this
                  customer's own grid. Without this the row read "—" and could not be bought. */}
              {r.price
                ? eur(r.price.finalUnitPriceHT)
                : r.indicativePriceHT != null
                  ? eur(r.indicativePriceHT)
                  : '—'}{' '}
              <span className="unit">{t('portal.catalogue.exclVat')}</span>
            </div>
            {hasDiscount && (
              <>
                <div className="listprice tnum">{eur(r.price!.unitPriceHT)}</div>
                <div className="remise">
                  {t('portal.catalogue.contractDiscount', { pct: pct(r.price!.discountPct) })}
                </div>
              </>
            )}
          </div>
          <div className="actions" style={{ marginTop: 'var(--s-2)' }}>
            <input
              className="qty tnum"
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
              aria-label={t('portal.catalogue.quantityFor', { sku: r.sku })}
              disabled={r.avail === 'out'}
            />
            <button
              className="iconbtn iconbtn--accent"
              aria-label={t('portal.catalogue.addToCartAria', { sku: r.sku })}
              onClick={() => onAdd(qty)}
              disabled={busy || (!r.price && r.indicativePriceHT == null)}
              title={
                r.price || r.indicativePriceHT != null
                  ? t('portal.catalogue.addToCart')
                  : t('portal.catalogue.notAvailable')
              }
            >
              <ShoppingCart size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="sublinks">
        <button onClick={() => navigate(`/catalog/${r.id}`)}>
          {t('portal.catalogue.moreInformation')}
        </button>
        <button onClick={() => navigate(`/catalog/${r.id}`)}>
          {t('portal.catalogue.crossReferences')}
        </button>
        <button onClick={() => navigate(`/catalog/${r.id}`)}>
          {t('portal.catalogue.oeNumbers')}
        </button>
        <button onClick={() => navigate(`/catalog/${r.id}`)}>
          {t('portal.catalogue.applications')}
        </button>
      </div>

      {/* MULTI-SOURCE COMPOSER — same control the counter has.
          A buyer who searched a reference and found it in two places should be able to split the
          order the way it suits them: take the two we hold today, order the third for Monday. The
          three columns are exactly the three things that decide it — availability, price, date. */}
      {origins.length > 1 && (
        <div className="origins">
          <div className="ohead" aria-hidden="true">
            <span>{t('portal.catalogue.source')}</span>
            <span className="ar">{t('portal.catalogue.avail')}</span>
            <span>{t('portal.catalogue.youGetIt')}</span>
            <span className="ar">{t('portal.catalogue.unitPrice')}</span>
            <span className="ar">{t('portal.catalogue.qty')}</span>
          </div>
          {origins.map((o) => {
            // Our promise for the quantity asked; the supplier's own date for it sits in the tooltip.
            const eta =
              o.source === 'EXTERNAL'
                ? promiseEta(r.promiseRule, cal, nowMs, {
                    slots: r.deliverySlots,
                    qty: originQty.EXTERNAL || 1,
                    leadDays: r.leadDays,
                  })
                : o.eta;
            return (
              <div className="orow" key={o.source}>
                <span className={`obadge ${o.source === 'INTERNAL' ? 'is-ours' : 'is-supplier'}`}>
                  {o.source === 'INTERNAL'
                    ? t('portal.catalogue.inStock')
                    : t('portal.catalogue.onOrder')}
                </span>
                <span className="oqty tnum">{o.available > 10 ? '>10' : o.available}</span>
                <span className={`owhen${eta.immediate ? ' is-now' : ''}`} title={eta.detail}>
                  {eta.immediate ? t('portal.catalogue.rightNow') : eta.label}
                </span>
                <span className="oprice tnum">{eur(o.unitPriceHT)}</span>
                <span className="oadd">
                  <input
                    className="qty tnum"
                    type="number"
                    min={0}
                    max={o.available}
                    value={originQty[o.source] ?? 0}
                    onChange={(e) =>
                      setOriginQty((q) => ({
                        ...q,
                        [o.source]: Math.max(0, Math.min(o.available, Number(e.target.value))),
                      }))
                    }
                    aria-label={
                      o.source === 'INTERNAL'
                        ? t('portal.catalogue.quantityFromStock', { sku: r.sku })
                        : t('portal.catalogue.quantityOnOrder', { sku: r.sku })
                    }
                  />
                </span>
              </div>
            );
          })}
          <div className="ofoot">
            <button
              className="btn btn--primary"
              disabled={composedUnits === 0}
              onClick={() => {
                for (const o of origins) {
                  const n = originQty[o.source] ?? 0;
                  if (n > 0) onAddOrigin(o.source, n, o.unitPriceHT);
                }
                setOriginQty({});
              }}
            >
              {t('portal.catalogue.add')}{' '}
              {composedUnits > 0 ? t('portal.catalogue.addUnits', { count: composedUnits }) : ''}
            </button>
            {composedOrigins > 1 && (
              <span className="onote">
                {t('portal.catalogue.deliveries', { count: composedOrigins })}
              </span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
